import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { ContextGap, GapPriority } from '@/types/api'

interface GapCardProps {
  gap: ContextGap
  /** When provided, the "Fill this gap" link uses this href; otherwise we
   * default to /fill-gaps?gapId={id}. */
  fillHref?: string
}

const SEVERITY_DOT: Record<GapPriority, string> = {
  critical: 'bg-error',
  high: 'bg-warning',
  medium: 'bg-info',
  low: 'bg-text-tertiary',
}

const SEVERITY_LABEL: Record<GapPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

/**
 * Single gap card: severity dot, title, description, domain tag, fill CTA.
 * Title falls back to an inferred label if the backend hasn't shipped the
 * `title` field yet (open Vidit flag) so the card never renders blank.
 */
export function GapCard({ gap, fillHref }: GapCardProps) {
  const priority = (gap.priority ?? 'medium') as GapPriority
  const href = fillHref ?? `/fill-gaps?gapId=${encodeURIComponent(gap.id)}`
  const title = gap.title?.trim() || titleFallback(gap)

  return (
    <article
      data-testid={`gap-card-${gap.id}`}
      className="flex items-start gap-3 rounded-lg border border-border-default bg-bg-primary p-4 hover:border-border-hover transition-colors"
    >
      <span
        aria-label={`${SEVERITY_LABEL[priority]} priority`}
        className={cn(
          'w-2 h-2 rounded-full shrink-0 mt-1.5',
          SEVERITY_DOT[priority]
        )}
      />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-display font-semibold text-text-primary">
          {title}
        </h3>
        {gap.description && (
          <p className="mt-1 text-sm text-text-secondary leading-relaxed">
            {gap.description}
          </p>
        )}
        {gap.domain && (
          <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs text-text-tertiary bg-bg-elevated border border-border-default">
            {humanizeDomain(gap.domain)}
          </span>
        )}
      </div>
      <Button
        asChild
        size="sm"
        className="shrink-0"
      >
        <Link to={href}>Fill this gap</Link>
      </Button>
    </article>
  )
}

function titleFallback(gap: ContextGap): string {
  if (gap.domain) return `Gap in ${humanizeDomain(gap.domain)}`
  return 'Open gap'
}

function humanizeDomain(domain: string): string {
  return domain.replace(/_/g, ' ')
}
